import { ChevronRightDoubleIcon } from '@langchain/untitled-ui-icons';
import * as RadixDialog from '@radix-ui/react-dialog';

import { CSSProperties, ReactNode, createContext, useContext } from 'react';

import { IconButton } from '@/design-system';
import { ScrollParentProvider } from '@/utils/VirtuosoCustomScrollParentContext';
import {
  ScrollParentContext,
  useScrollParent,
} from '@/utils/VirtuosoCustomScrollParentContext/useScrollParent';

import { cn } from '../utils/tailwind';
import { TopBarPaneSlot } from './Pane.utils';

const PaneDepth = createContext<number>(1);

interface CorePaneProps {
  open: boolean;
  onClose: () => void;
  children: ReactNode;
  dialogStyle?: CSSProperties;
  transparentBackdrop?: boolean;
  handleScroll?: (e: React.UIEvent<HTMLDivElement>) => void;
  preventInteractOutside?: boolean;
  overlayClassName?: string;
}

interface PaneProps extends CorePaneProps {
  title: ReactNode;
  noBackArrow?: boolean;
  className?: string;
  titleClassName?: string;
  topBarRightElement?: ReactNode;
  onBack?: () => void;
}

function PlainPane({
  open,
  onClose,
  children,
  dialogStyle,
  transparentBackdrop,
  handleScroll,
  preventInteractOutside,
  overlayClassName,
}: CorePaneProps) {
  const depth = useContext(PaneDepth);
  const scrollParentRef = useScrollParent();

  return (
    <RadixDialog.Root
      open={open}
      onOpenChange={(o) => {
        if (!o) onClose();
      }}
    >
      <RadixDialog.Portal>
        <RadixDialog.Overlay
          className={cn(
            'fixed inset-0 z-10 h-screen bg-black/30 dark:bg-black/40',
            // we need this hold because the overlay disappears without it
            'data-[state=closed]:animate-hold',
            transparentBackdrop ? 'bg-transparent dark:bg-transparent' : '',
            overlayClassName
          )}
        >
          <RadixDialog.Content
            ref={scrollParentRef}
            className={cn(
              'relative flex h-screen flex-col items-stretch overflow-y-auto rounded-l bg-background shadow-xl',
              'data-[state=open]:animate-slide-left data-[state=closed]:animate-slide-right focus:outline-none focus-visible:outline-none'
            )}
            style={{
              marginLeft: `${depth * 5.0}rem`,
              ...dialogStyle,
            }}
            onScroll={handleScroll}
            onOpenAutoFocus={(e) => {
              // Prevent default auto-focus behavior to avoid showing tooltip on close button
              e.preventDefault();
              // Focus the dialog content itself instead - it's focusable with tabIndex
              const target = e.currentTarget as HTMLDivElement;
              if (target) {
                target.focus();
              }
            }}
            onPointerDownOutside={
              preventInteractOutside
                ? (e) => {
                    e.preventDefault();
                  }
                : undefined
            }
            onInteractOutside={
              preventInteractOutside
                ? (e) => {
                    e.preventDefault();
                  }
                : undefined
            }
          >
            <TopBarPaneSlot.Context>
              <PaneDepth.Provider value={depth + 1}>
                {children}
              </PaneDepth.Provider>
            </TopBarPaneSlot.Context>
          </RadixDialog.Content>
        </RadixDialog.Overlay>
      </RadixDialog.Portal>
    </RadixDialog.Root>
  );
}

interface PaneProps extends CorePaneProps {
  title: ReactNode;
  noBackArrow?: boolean;
  className?: string;
  titleClassName?: string;
  topBarRightElement?: ReactNode;
  scrollRef?: React.RefObject<HTMLDivElement>;
}

export function Pane({
  open,
  onClose,
  onBack,
  title,
  children,
  className,
  noBackArrow,
  titleClassName,
  topBarRightElement,
  dialogStyle,
  transparentBackdrop,
  scrollRef,
  handleScroll,
  preventInteractOutside,
  overlayClassName,
}: PaneProps) {
  const paneComponent = (
    <PlainPane
      open={open}
      onClose={onClose}
      dialogStyle={dialogStyle}
      transparentBackdrop={transparentBackdrop}
      handleScroll={handleScroll}
      preventInteractOutside={preventInteractOutside}
      overlayClassName={overlayClassName}
    >
      <RadixDialog.Title
        className={cn(
          'sticky top-0 z-10 flex min-h-[77px] items-center justify-between border-b border-b-secondary bg-background py-4 pl-6 text-lg font-semibold text-secondary',
          titleClassName
        )}
        data-testid={`pane-header-${title}`}
      >
        {!noBackArrow && (
          <IconButton
            label="Close"
            icon={ChevronRightDoubleIcon}
            onClick={onBack ?? onClose}
            variant="plain"
            color="secondary"
            size="sm"
            iconClassName="size-6"
            className="mr-3"
          />
        )}
        {title}
        <span className="flex-grow" />
        {topBarRightElement}
        <TopBarPaneSlot.Slot />
      </RadixDialog.Title>
      <div className={cn('flex-1 px-16 pb-16 pt-6', className)}>{children}</div>
    </PlainPane>
  );
  return scrollRef ? (
    <ScrollParentContext.Provider value={scrollRef}>
      {paneComponent}
    </ScrollParentContext.Provider>
  ) : (
    <ScrollParentProvider>{paneComponent}</ScrollParentProvider>
  );
}
